import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Box, Typography, Paper, TextField, Tooltip } from "@mui/material";
import { formatIsoToCustomDateStringWithEEEEHHMMA, textStateColors, textStateIcons } from "../utils/utils";
import { useSelector } from "react-redux";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { FrontendUser, Organization, TextThreadMessage, WaitlistSlotState, WaitlistSlotStateChange } from "../types";
import { useSnackbar } from "../providers/SnackbarProvider";
import { RootState } from "../store";
import LoadingWithMessage from "../subcomponents/LoadingWithMessage";
import { Colors } from "../Colors";
import { PrimaryButton } from "../subcomponents/CustomButton";
import SendIcon from "@mui/icons-material/Send";
import WarningIcon from "@mui/icons-material/Warning";
import { usePostHog } from "posthog-js/react";
import { DateTime } from "luxon";

const Container = styled(Box)`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  max-width: 100%;
`;

const TextThreadContainer = styled(Box)<{ $maxHeight?: string }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  max-height: ${({ $maxHeight }) => $maxHeight || "80vh"};
  min-height: 0;
  padding: 16px;
  background: ${Colors.bg};
  border-radius: 8px;
  border: 1px solid ${Colors.border};
`;

const TextMetadata = styled(Typography)<{ role: string }>`
  font-size: 0.65rem;
  color: ${Colors.grey1};
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const roleToColor = {
  agent: Colors.primary,
  patient: Colors.bgTextMsg,
  clinic: Colors.primary,
  network: Colors.primary,
  tool: Colors.bg3,
};

interface TextMessageProps {
  role: "agent" | "patient" | "clinic" | "network" | "tool";
  error: string | undefined;
}

const TextMessage = styled(({ role, error, ...otherProps }: TextMessageProps & React.ComponentProps<typeof Paper>) => <Paper {...otherProps} />)`
  margin-left: ${(props: TextMessageProps) => (props.role === "patient" ? "0px" : "auto")};
  margin-right: ${(props: TextMessageProps) => (props.role === "patient" ? "auto" : "0px")};
  padding: 12px 16px;
  max-width: 65%;
  width: fit-content;
  background-color: ${(props: TextMessageProps) => props.error ? Colors.error : roleToColor[props.role as keyof typeof roleToColor]} !important;
  color: #fff !important;
  border-radius: 12px !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2) !important;
  box-sizing: border-box;
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-word;
  white-space: pre-wrap;
  position: relative;
`;

const InputContainer = styled(Box)`
  padding: 16px 0 0 0;
  display: flex;
  gap: 8px;
  align-items: flex-end;
`;

interface TextThreadDisplayProps {
  thread: TextThreadMessage[];
  agentId: string;
  patientId: string;
  waitlistSlotIds: { waitlistSlotId: string; appointmentDate: string }[];
  waitlistSlotStateChanges: WaitlistSlotStateChange[];
  phoneNumber?: string;
  messageContainerMaxHeight?: string;
}

const TextThreadDisplay: React.FC<TextThreadDisplayProps> = ({
  thread,
  agentId,
  patientId,
  phoneNumber,
  waitlistSlotIds,
  waitlistSlotStateChanges,
  messageContainerMaxHeight,
}) => {
  const user: FrontendUser | null = useSelector((state: RootState) => state.auth.user);
  const organization: Organization | null = useSelector((state: RootState) => state.auth.organization);
  const [threadLoading, setThreadLoading] = useState(false);
  const posthog = usePostHog();
  const userTimeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const location = useLocation();
  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null);

  const { showMessage } = useSnackbar();

  const [newMessage, setNewMessage] = useState("");

  const scrollableRef = useRef<HTMLDivElement | null>(null);
  const messageRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // Track if we've scrolled to a highlighted message
  const [hasScrolledToHighlighted, setHasScrolledToHighlighted] = useState(false);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const messageId = queryParams.get("messageId");

    if (messageId) {
      setHighlightedMessageId(messageId);
      setHasScrolledToHighlighted(false); // Reset this flag when we get a new messageId

      // Clear the highlight after 5 seconds, but don't scroll
      const timer = setTimeout(() => {
        setHighlightedMessageId(null);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [location.search]);

  // Separate effect for initial scrolling to highlighted message
  useEffect(() => {
    if (highlightedMessageId && messageRefs.current[highlightedMessageId] && !hasScrolledToHighlighted) {
      // If we have a highlighted message, scroll to it
      messageRefs.current[highlightedMessageId]?.scrollIntoView({ behavior: "smooth", block: "center" });
      setHasScrolledToHighlighted(true);
    }
  }, [highlightedMessageId, hasScrolledToHighlighted]);

  // Separate effect for scrolling to bottom on new messages
  useEffect(() => {
    const scrollableElement = scrollableRef.current;
    const queryParams = new URLSearchParams(location.search);
    const messageId = queryParams.get("messageId");

    // Only scroll to bottom if no messageId is present in URL
    if (scrollableElement && !messageId) {
      scrollableElement.scrollTop = scrollableElement.scrollHeight;
    }
  }, [thread, location.search]);

  const handleSendCustomText = async () => {
    if (!user?.userId) {
      return;
    }
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/texts/send`,
        {
          patientId: patientId,
          message: newMessage,
        },
        {
          headers: {
            Authorization: `Bearer ${user?.token}`,
          },
        }
      );

      if (response.status === 200) {
        showMessage("Text sent successfully", "success");
      } else {
        throw new Error(`Failed to send custom text to patient`);
      }
    } catch (error: any) {
      if (error.response?.status === 401) {
        showMessage("Session has expired", "error");
      } else {
        console.error("Error sending custom text:", error);
        // showMessage('Failed to send custom text', 'error');
      }
    }
  };

  const handleSendNewMessage = async () => {
    if (newMessage.trim() === "") return;
    setNewMessage("");

    await handleSendCustomText();
  };

  return (
    <>
      <Container>
        <Typography variant={"h6"} sx={{ marginBottom: 2, fontWeight: 600, color: Colors.white }}>
          Text Conversation
        </Typography>
        {threadLoading ? (
          <LoadingWithMessage message="Loading thread" size={60} />
        ) : (
          <TextThreadContainer ref={scrollableRef} $maxHeight={messageContainerMaxHeight}>
            {thread.map((message: TextThreadMessage, index) => {
              const stateChangesForMessage = waitlistSlotStateChanges.filter(
                (stateChange: WaitlistSlotStateChange) => stateChange.textThreadMessageId === message.textThreadMessageId
              );

              const isPatientMessage = message.role === "patient";
              const isHighlighted = message.textThreadMessageId === highlightedMessageId;

              // Create the status icon if needed
              const statusIcon = isPatientMessage
                ? (() => {
                    const filteredStateChanges = stateChangesForMessage
                      .filter((stateChange) => stateChange.state === WaitlistSlotState.accepted || stateChange.state === WaitlistSlotState.declined)
                      .slice(0, 1);

                    if (filteredStateChanges.length > 0) {
                      const stateChange = filteredStateChanges[0];
                      const appointment = waitlistSlotIds.find((slot) => slot.waitlistSlotId === stateChange.waitlistSlotId);
                      const appointmentDate = appointment?.appointmentDate
                        ? formatIsoToCustomDateStringWithEEEEHHMMA(appointment.appointmentDate)
                        : "Unknown date";

                      return (
                        <Tooltip
                          title={`Appointment Date: ${appointmentDate} - ${stateChange.stateJustification}`}
                          placement="right"
                          arrow
                          onMouseEnter={() => {
                            posthog.capture("[PENCILED] User hovered over state change", {
                              state: stateChange.state,
                              justification: stateChange.stateJustification,
                              waitlistSlotId: stateChange.waitlistSlotId,
                              appointmentDate: appointmentDate,
                              page: window.location.href.includes("patient") ? "PatientInfoPage" : "WaitlistSlotInfo",
                              orgName: organization?.name,
                              organizationId: organization?.organizationId,
                            });
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: 28,
                              height: 28,
                              borderRadius: "6px",
                              backgroundColor: Colors.bg2,
                              border: `1px solid ${Colors.border}`,
                              color: textStateColors[stateChange.state as unknown as keyof typeof textStateColors],
                              flexShrink: 0,
                            }}
                          >
                            {textStateIcons[stateChange.state as unknown as keyof typeof textStateIcons]}
                          </Box>
                        </Tooltip>
                      );
                    } else {
                      return <Box sx={{ width: 28, flexShrink: 0 }} />;
                    }
                  })()
                : null;
              
              const error = message.error ? `${message.error} (${message.patientPhoneNumber})` : (phoneNumber && message.patientPhoneNumber !== phoneNumber ? `Phone number has changed (${message.patientPhoneNumber})` : undefined);
              
              return (
                <Box
                  key={index}
                  ref={(el: HTMLDivElement | null) => {
                    if (message.textThreadMessageId) {
                      messageRefs.current[message.textThreadMessageId] = el;
                    }
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    justifyContent: isPatientMessage ? "flex-start" : "flex-end",
                    marginBottom: 2,
                    transition: "background-color 1s ease-out",
                    backgroundColor: isHighlighted ? "rgba(94, 120, 238, 0.15)" : "transparent",
                    borderRadius: "8px",
                    padding: isHighlighted ? "8px" : "0",
                  }}
                >
                  {isPatientMessage && statusIcon}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                      alignItems: isPatientMessage ? "flex-start" : "flex-end",
                      maxWidth: "75%",
                    }}
                  >
                    <TextMessage role={message.role} error={error}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontSize: "0.9rem",
                          lineHeight: 1.5,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {message.message}
                      </Typography>
                      {error && (
                        <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
                          <WarningIcon sx={{ fontSize: "1rem", color: Colors.warning }} />
                          <Typography variant="caption" sx={{ color: Colors.warning, fontSize: "0.75rem" }}>
                            {error}
                          </Typography>
                        </Box>
                      )}
                    </TextMessage>
                    {message.date && (
                      <TextMetadata variant="caption" role={message.role}>
                        <span>{formatIsoToCustomDateStringWithEEEEHHMMA(DateTime.fromISO(message.date).setZone(userTimeZone).toISO()!)}</span>
                        <Box
                          component="span"
                          sx={{
                            fontSize: "0.6rem",
                            fontWeight: 600,
                            color: Colors.grey1,
                            backgroundColor: Colors.bg3,
                            padding: "2px 6px",
                            borderRadius: "4px",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          {message.role}
                        </Box>
                      </TextMetadata>
                    )}
                  </Box>
                </Box>
              );
            })}
          </TextThreadContainer>
        )}
        <InputContainer>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Type a message..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSendNewMessage();
              }
            }}
            multiline
            minRows={1}
            maxRows={6}
            sx={{
              "& .MuiInputBase-input": {
                color: Colors.white,
                fontSize: "0.9rem",
              },
              "& .MuiInputBase-input::placeholder": {
                color: Colors.grey2,
                opacity: 0.7,
              },
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                backgroundColor: Colors.bg2,
                border: `1px solid ${Colors.border}`,
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: Colors.primary,
                },
                "&.Mui-focused": {
                  borderColor: Colors.primary,
                  boxShadow: `0 0 0 2px rgba(94, 120, 238, 0.1)`,
                },
                "& fieldset": {
                  border: "none",
                },
              },
            }}
          />

          <PrimaryButton
            variant="contained"
            disabled={!newMessage.trim()}
            onClick={() => {
              handleSendNewMessage();
              posthog.capture("[PENCILED] User sent manual text", {
                ...(waitlistSlotIds.length === 1 ? { waitlistSlotId: waitlistSlotIds[0].waitlistSlotId } : {}),
                patientId: patientId,
                agentId: agentId,
                message: newMessage,
                orgName: organization?.name,
                orgId: organization?.organizationId,
              });
            }}
            sx={{
              borderRadius: "8px",
              minWidth: "58px",
              height: "58px",
              padding: "12px",
              "&:disabled": {
                opacity: 0.4,
              },
            }}
          >
            <SendIcon sx={{ fontSize: "1.2rem" }} />
          </PrimaryButton>
        </InputContainer>
      </Container>
    </>
  );
};

export default TextThreadDisplay;
